import { useEffect, useRef, useState } from "react";
import { Download, Upload, Loader2, CheckCircle2, XCircle, AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, API_BASE, getToken } from "@/lib/api";
import { authenticatedDownload, useBulkJob } from "./useBulkJob";

const STEP_PICK_FILE = "pick_file";
const STEP_PREVIEW = "preview";
const STEP_RUNNING = "running";
const STEP_DONE = "done";

export default function ImportDialog({
  masterKey, label, open, onOpenChange, onDone,
}) {
  const [step, setStep] = useState(STEP_PICK_FILE);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("upsert");
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const inputRef = useRef(null);

  const { job, done } = useBulkJob(jobId, { enabled: !!jobId });

  useEffect(() => {
    if (!open) {
      // reset when closed
      setStep(STEP_PICK_FILE);
      setFile(null);
      setPreview(null);
      setMode("upsert");
      setJobId(null);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (job && done) setStep(STEP_DONE);
  }, [job, done]);

  const downloadTemplate = async (format) => {
    try {
      await authenticatedDownload(
        `/bulk/${masterKey}/template?format=${format}`,
        `${masterKey}_template.${format}`,
      );
      toast.success(`Template downloaded (${format})`);
    } catch (e) {
      toast.error(`Template failed: ${e?.message || e}`);
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    doPreview(f);
  };

  const doPreview = async (f) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post(`/bulk/${masterKey}/preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStep(STEP_PREVIEW);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const doStart = async () => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      form.append("dry_run", dryRun ? "true" : "false");
      const { data } = await api.post(`/bulk/${masterKey}/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobId(data.job_id);
      setStep(STEP_RUNNING);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed to start");
    } finally {
      setBusy(false);
    }
  };

  const runForReal = async () => {
    // Same file, dry_run=false, keep mode.
    setStep(STEP_PICK_FILE);
    setDryRun(false);
    setJobId(null);
    // Requeue via doStart with dryRun off — small delay so state settles.
    setTimeout(async () => {
      setBusy(true);
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("mode", mode);
        form.append("dry_run", "false");
        const { data } = await api.post(`/bulk/${masterKey}/import`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setJobId(data.job_id);
        setStep(STEP_RUNNING);
      } finally {
        setBusy(false);
      }
    }, 0);
  };

  const closeAndNotify = () => {
    if (onDone) onDone(job);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[720px] w-[92vw] max-h-[85vh] overflow-hidden p-0 flex flex-col"
        data-testid={`import-dialog-${masterKey}`}
      >
        <DialogHeader className="p-6 pb-3 border-b border-neutral-200 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#E5197F]" />
            Import {label}
          </DialogTitle>
          <DialogDescription>
            Bulk create or update {label.toLowerCase()} from Excel or CSV.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4" data-testid="import-dialog-scroll">
          <ImportDialogBody
            step={step} setStep={setStep}
            preview={preview} file={file}
            busy={busy}
            mode={mode} setMode={setMode}
            dryRun={dryRun} setDryRun={setDryRun}
            job={job} masterKey={masterKey}
            onPick={pickFile} inputRef={inputRef}
            downloadTemplate={downloadTemplate}
            onStart={doStart}
            onRunForReal={runForReal}
            onClose={closeAndNotify}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Body split-out so the DialogContent can host a sticky footer per step.
// ---------------------------------------------------------------------------
function ImportDialogBody({
  step, setStep, preview, file, busy, mode, setMode, dryRun, setDryRun,
  job, masterKey, onPick, inputRef, downloadTemplate,
  onStart, onRunForReal, onClose,
}) {
  return (
    <>
        {step === STEP_PICK_FILE && (
          <div className="space-y-5">
            <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-5">
              <div className="text-sm font-semibold text-neutral-800 mb-2">
                Step 1 — Download a template
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadTemplate("xlsx")}
                  data-testid={`import-template-xlsx-${masterKey}`}
                >
                  <Download className="h-4 w-4 mr-2" /> Excel (.xlsx)
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadTemplate("csv")}
                  data-testid={`import-template-csv-${masterKey}`}
                >
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
              </div>
            </div>

            <div
              className="rounded-xl border-2 border-dashed border-neutral-300 hover:border-[#E5197F]/60 hover:bg-[#E5197F]/5 transition-colors p-8 text-center cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
              onDragOver={(e) => e.preventDefault()}
              data-testid={`import-dropzone-${masterKey}`}
            >
              <FileText className="h-8 w-8 mx-auto text-neutral-400 mb-3" />
              <div className="text-sm font-medium text-neutral-800">
                Step 2 — Click or drop your filled file here
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                Accepts .xlsx or .csv
              </div>
              <input
                type="file"
                ref={inputRef}
                accept=".xlsx,.csv"
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0])}
                data-testid={`import-file-input-${masterKey}`}
              />
            </div>
            {busy && (
              <div className="text-xs text-neutral-500 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Parsing…
              </div>
            )}
          </div>
        )}

        {step === STEP_PREVIEW && preview && (
          <PreviewStep
            preview={preview} file={file} busy={busy}
            mode={mode} setMode={setMode}
            dryRun={dryRun} setDryRun={setDryRun}
            masterKey={masterKey}
            onBack={() => setStep(STEP_PICK_FILE)}
            onStart={onStart}
          />
        )}

        {step === STEP_RUNNING && (
          <ProgressView job={job} />
        )}

        {step === STEP_DONE && (
          <DoneView job={job} masterKey={masterKey} onClose={onClose}
                    onRunForReal={job?.status === "dry_run_completed" ? onRunForReal : null} />
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PreviewStep with sticky footer + horizontally scrollable table + collapsible
// column-requirements accordion (UAT B4).
// ---------------------------------------------------------------------------
function PreviewStep({ preview, file, busy, mode, setMode, dryRun, setDryRun,
                       masterKey, onBack, onStart }) {
  const [showReqs, setShowReqs] = useState(false);
  const total = preview?.total_rows ?? preview?.preview_rows?.length ?? 0;
  const shown = preview?.preview_rows?.length ?? 0;
  return (
    <div className="space-y-4">
      <div className="text-sm text-neutral-700 flex items-center gap-2 flex-wrap">
        <FileText className="h-4 w-4" />
        <span className="truncate max-w-[260px]" title={file?.name}>{file?.name}</span>
        <span>·</span>
        <span data-testid="import-preview-count">Showing {shown} of {total} rows</span>
      </div>

      {/* Column requirements — collapsed accordion */}
      <button
        type="button"
        onClick={() => setShowReqs((s) => !s)}
        className="text-xs px-3 py-1.5 rounded border border-neutral-200 bg-neutral-50 hover:bg-neutral-100"
        data-testid="import-col-reqs-toggle"
      >
        {showReqs ? "Hide" : "Show"} column requirements
      </button>
      {showReqs && (
        <div className="rounded-lg border border-neutral-200 p-3 text-xs bg-neutral-50 max-h-48 overflow-y-auto" data-testid="import-col-reqs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {(preview?.column_hints || preview?.header || []).map((h, i) => (
              <div key={i} className="truncate" title={typeof h === "string" ? h : JSON.stringify(h)}>
                {typeof h === "string" ? h : (h.label || h.key)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data preview table with visible scrollbars */}
      <div
        className="rounded-xl border border-neutral-200 overflow-auto"
        style={{ maxHeight: 280 }}
        data-testid="import-preview-scroll"
      >
        <Table>
          <TableHeader className="bg-neutral-50 sticky top-0 z-10">
            <TableRow>
              {preview.header.map((h, i) => (
                <TableHead key={i} className="text-[11px] uppercase tracking-wide whitespace-nowrap">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {preview.preview_rows.map((row, ri) => (
              <TableRow key={ri}>
                {row.map((v, ci) => {
                  const s = v === null || v === undefined ? "" : String(v);
                  return (
                    <TableCell key={ci} className="text-xs max-w-[180px] whitespace-nowrap overflow-hidden text-ellipsis" title={s}>
                      {s}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Sticky footer with mode selector + dry-run + Back/Start */}
      <div className="sticky bottom-0 -mx-6 px-6 pt-3 pb-3 bg-white border-t border-neutral-200 flex items-center gap-3 flex-wrap"
           data-testid="import-sticky-footer">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-neutral-500" title="Upsert: create+update. Create only: reject dupes. Update only: reject new rows.">
            Mode
          </Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="h-9 w-[200px]"
                            data-testid={`import-mode-select-${masterKey}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upsert">Upsert (create + update)</SelectItem>
              <SelectItem value="create_only">Create only</SelectItem>
              <SelectItem value="update_only">Update only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="h-9 flex items-center gap-2 px-3 rounded-md border border-neutral-200 cursor-pointer bg-white hover:border-[#E5197F]/60"
                data-testid={`import-dry-run-label-${masterKey}`}>
          <input type="checkbox" checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  data-testid={`import-dry-run-${masterKey}`} />
          <span className="text-sm font-medium">Dry-run first</span>
        </label>
        <div className="flex-1" />
        <Button variant="outline" onClick={onBack} data-testid="import-back">Back</Button>
        <Button
          onClick={onStart}
          disabled={busy}
          className="tulsi-gradient text-white font-semibold"
          data-testid={`import-start-${masterKey}`}
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Start import
        </Button>
      </div>
    </div>
  );
}

function ProgressView({ job }) {
  const pct = job?.total_rows
    ? Math.min(100, Math.round((job.processed / job.total_rows) * 100))
    : 0;
  return (
    <div className="space-y-4 py-2" data-testid="import-progress">
      <div className="flex items-center gap-2 text-sm text-neutral-700">
        <Loader2 className="h-4 w-4 animate-spin text-[#E5197F]" /> Importing…
      </div>
      <div className="h-2 w-full bg-neutral-100 rounded-full overflow-hidden">
        <div
          className="h-full tulsi-gradient transition-all"
          style={{ width: `${pct}%` }}
          data-testid="import-progress-bar"
        />
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <Stat label="Processed" value={`${job?.processed ?? 0}/${job?.total_rows ?? 0}`} testid="import-stat-processed" />
        <Stat label="Created" value={job?.created ?? 0} testid="import-stat-created" />
        <Stat label="Updated" value={job?.updated ?? 0} testid="import-stat-updated" />
        <Stat label="Errors" value={job?.error_count ?? 0} testid="import-stat-errors" />
      </div>
    </div>
  );
}

function DoneView({ job, masterKey, onClose, onRunForReal }) {
  const [downloading, setDownloading] = useState(false);
  const isDryRun = job?.status === "dry_run_completed";
  const isCleanDone = job?.status === "completed" && (job?.error_count ?? 0) === 0;
  const failed = job?.status === "failed";

  const downloadErrors = async () => {
    if (!job?.id) return;
    setDownloading(true);
    try {
      const now = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      await authenticatedDownload(
        `/bulk/${masterKey}/import/${job.id}/errors.xlsx`,
        `${masterKey}_import_errors_${now}.xlsx`,
      );
      toast.success("Error report downloaded");
    } catch (e) {
      toast.error(`Error report download failed: ${e?.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="import-done">
      {isDryRun && (
        <div className="rounded-xl border-2 border-sky-300 bg-sky-50 p-4" data-testid="import-dry-run-banner">
          <div className="text-sm font-bold text-sky-900">This was a dry-run — no data has been saved.</div>
          <div className="text-xs text-sky-800 mt-1">
            The same validations ran as a real import. Review the counts and error report,
            then click <strong>Run for real</strong> to commit changes.
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 text-sm">
        {failed && <XCircle className="h-5 w-5 text-red-500" />}
        {isCleanDone && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
        {!failed && !isCleanDone && !isDryRun && <AlertCircle className="h-5 w-5 text-amber-500" />}
        <span className="font-semibold">
          {failed ? "Import failed" :
            isDryRun ? "Dry-run completed" :
              isCleanDone ? "All rows imported cleanly" :
                `Import completed with ${job?.error_count} error${job?.error_count === 1 ? "" : "s"}`}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <Stat label="Total" value={job?.total_rows ?? 0} testid="import-final-total" />
        <Stat label="Created" value={job?.created ?? 0} testid="import-final-created" />
        <Stat label="Updated" value={job?.updated ?? 0} testid="import-final-updated" />
        <Stat label="Errors" value={job?.error_count ?? 0} testid="import-final-errors" />
      </div>
      {(job?.error_count ?? 0) > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-3" data-testid="import-error-review">
          {(job?.error_reason_top || []).length > 0 && (
            <div className="space-y-1" data-testid="import-error-reasons">
              <div className="text-xs font-semibold text-red-900 uppercase tracking-wide">
                Top {job.error_reason_top.length} error reasons
              </div>
              <ul className="text-xs text-red-800 space-y-0.5">
                {job.error_reason_top.map((r, i) => (
                  <li key={i} data-testid={`import-error-reason-${i}`}>
                    <span className="font-semibold">{r.reason}:</span> {r.count} row{r.count === 1 ? "" : "s"}
                    {r.sample_row_no ? <span className="text-red-700"> — e.g. row {r.sample_row_no}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-sm font-semibold text-red-800">
            Download the full error report for row-by-row details.
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-red-300 text-red-700 bg-white"
            disabled={downloading}
            data-testid="import-error-download"
            onClick={downloadErrors}
          >
            {downloading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            {downloading ? "Preparing…" : "Download error report"}
          </Button>
        </div>
      )}
      <DialogFooter className="gap-2">
        {onRunForReal && (
          <Button
            onClick={onRunForReal}
            className="tulsi-gradient text-white font-semibold"
            data-testid="import-run-for-real"
          >
            Run for real
          </Button>
        )}
        <Button
          onClick={onClose}
          variant={onRunForReal ? "outline" : "default"}
          className={onRunForReal ? "" : "tulsi-gradient text-white font-semibold"}
          data-testid="import-close"
        >
          {onRunForReal ? "Cancel" : "Close"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-lg font-extrabold text-neutral-900" data-testid={testid}>{value}</div>
    </div>
  );
}
